// src/writer/components/ReorderableSection.tsx
import React, { useState, useRef } from 'react';
import { actionButtonClass, primaryButtonClass, secondaryButtonClass } from '../constants';

export interface ReorderableItem {
    id: string;
    label: string;
    content: string;
    isVisible: boolean;
    isRequired?: boolean;
    description?: string;
}

interface ReorderableSectionProps {
    title: string;
    items: ReorderableItem[];
    onReorder: (newOrder: ReorderableItem[]) => void;
    onToggleVisibility: (itemId: string) => void;
    onMoveToOtherSection?: (itemId: string) => void;
    otherSectionName?: string;
    className?: string;
}

const ReorderableSection: React.FC<ReorderableSectionProps> = ({
    title,
    items,
    onReorder,
    onToggleVisibility,
    onMoveToOtherSection,
    otherSectionName,
    className = ''
}) => {
    const [draggedItem, setDraggedItem] = useState<string | null>(null);
    const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
    const dragCounter = useRef(0);

    const handleDragStart = (e: React.DragEvent, itemId: string) => {
        setDraggedItem(itemId);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/html', itemId);
    };

    const handleDragOver = (e: React.DragEvent, index: number) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDragOverIndex(index);
    };

    const handleDragEnter = (e: React.DragEvent) => {
        e.preventDefault();
        dragCounter.current++;
    };

    const handleDragLeave = (e: React.DragEvent) => {
        dragCounter.current--;
        if (dragCounter.current === 0) {
            setDragOverIndex(null);
        }
    };

    const handleDrop = (e: React.DragEvent, dropIndex: number) => {
        e.preventDefault();
        dragCounter.current = 0;
        setDragOverIndex(null);

        if (!draggedItem) return;

        const draggedIndex = items.findIndex(item => item.id === draggedItem);
        if (draggedIndex === -1 || draggedIndex === dropIndex) return;

        const newItems = [...items];
        const [draggedItemData] = newItems.splice(draggedIndex, 1);
        newItems.splice(dropIndex, 0, draggedItemData);

        onReorder(newItems);
        setDraggedItem(null);
    };

    const handleDragEnd = () => {
        setDraggedItem(null);
        setDragOverIndex(null);
        dragCounter.current = 0;
    };

    const getItemStatusColor = (item: ReorderableItem) => {
        if (!item.isVisible) return 'bg-gray-100 text-gray-500 border-gray-200';
        if (item.content && item.content.trim().length > 0) return 'bg-green-50 text-green-800 border-green-200';
        return 'bg-blue-50 text-blue-800 border-blue-200';
    };

    const getItemStatusIcon = (item: ReorderableItem) => {
        if (!item.isVisible) return '👁️‍🗨️';
        if (item.content && item.content.trim().length > 0) return '✅';
        return '📝';
    };

    const getItemStatusText = (item: ReorderableItem) => {
        if (!item.isVisible) return 'Hidden';
        if (item.content && item.content.trim().length > 0) return 'Complete';
        return 'Empty';
    };

    return (
        <div className={`bg-white rounded-xl shadow-lg p-6 ${className}`}>
            <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-gray-800">{title}</h3>
                <div className="text-sm text-gray-600">
                    {items.filter(item => item.isVisible).length} visible, {items.filter(item => item.content?.trim()).length} completed
                </div>
            </div>

            <div className="space-y-3">
                {items.map((item, index) => (
                    <div
                        key={item.id}
                        draggable={!item.isRequired}
                        onDragStart={(e) => handleDragStart(e, item.id)}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDragEnter={handleDragEnter}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, index)}
                        onDragEnd={handleDragEnd}
                        className={`border-2 rounded-lg p-4 transition-all duration-200 ${
                            draggedItem === item.id
                                ? 'opacity-50 rotate-2'
                                : dragOverIndex === index
                                ? 'border-blue-400 bg-blue-50'
                                : getItemStatusColor(item)
                        } ${
                            !item.isRequired ? 'cursor-move hover:shadow-md' : 'cursor-default'
                        }`}
                    >
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3 flex-1">
                                {!item.isRequired && (
                                    <div className="text-gray-400 text-lg">⋮⋮</div>
                                )}
                                <div className="flex items-center space-x-2">
                                    <span className="text-lg">{getItemStatusIcon(item)}</span>
                                    <div>
                                        <h4 className="font-medium text-gray-800">{item.label}</h4>
                                        {item.description && (
                                            <p className="text-xs text-gray-600 mt-1">{item.description}</p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="flex items-center space-x-3">
                                <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700">
                                    {getItemStatusText(item)}
                                </span>
                                
                                {!item.isRequired && (
                                    <button
                                        onClick={() => onToggleVisibility(item.id)}
                                        className={`text-xs px-3 py-1 rounded ${
                                            item.isVisible 
                                                ? 'bg-gray-200 text-gray-700 hover:bg-gray-300' 
                                                : 'bg-blue-600 text-white hover:bg-blue-700'
                                        }`}
                                    >
                                        {item.isVisible ? 'Hide' : 'Show'}
                                    </button>
                                )}
                                
                                {onMoveToOtherSection && otherSectionName && !item.isRequired && (
                                    <button
                                        onClick={() => onMoveToOtherSection(item.id)}
                                        className="text-xs px-3 py-1 rounded bg-purple-100 text-purple-700 hover:bg-purple-200"
                                    >
                                        → {otherSectionName}
                                    </button>
                                )}
                            </div>
                        </div>

                        {dragOverIndex === index && draggedItem && draggedItem !== item.id && (
                            <div className="mt-2 h-1 bg-blue-400 rounded-full animate-pulse"></div>
                        )}
                    </div>
                ))}
            </div>

            {items.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                    <div className="text-4xl mb-2">📚</div>
                    <p>No sections configured yet</p>
                </div>
            )}

            <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="font-medium text-blue-800 mb-2">💡 Section Reordering Tips</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                    <li>• Drag and drop sections to reorder them</li>
                    <li>• Hide optional sections that don't apply to your book</li>
                    <li>• Required sections cannot be moved or hidden</li>
                    <li>• Move sections between Front Matter and Back Matter as needed</li>
                </ul>
            </div>
        </div>
    );
};

export default ReorderableSection;